import { useState } from 'react';
import {
  requestPresignedUrls,
  uploadFileToS3,
  notifyUploadComplete,
  type UploadedFileInfo,
} from '@/lib/api/s3-upload';
import type { InstructionSheetConverterResponse } from '@/lib/api/instruction-sheet-converter';

/// S3アップロード用カスタムフック

export interface UploadProgress {
  [filename: string]: number;
}

export interface UseS3UploadReturn {
  uploadFiles: (
    files: File[],
    accountId: string,
    authHeaders?: Record<string, string>,
  ) => Promise<InstructionSheetConverterResponse>;
  uploadProgress: UploadProgress;
  isUploading: boolean;
  error: string | null;
  clearError: () => void;
}

/**
 * S3アップロード用カスタムフック
 *
 * ## 機能概要
 * ファイルのS3直接アップロードを管理するReactフックです。
 * 進捗状態、エラー処理、アップロード完了通知まで一貫して処理します。
 *
 * ## 使用例
 * ```typescript
 * const { uploadFiles, uploadProgress, isUploading, error } = useS3Upload();
 *
 * const handleUpload = async () => {
 *   try {
 *     const response = await uploadFiles(files, accountId, authHeaders);
 *     console.log('変換開始:', response.job_id);
 *   } catch (err) {
 *     console.error('アップロード失敗:', err);
 *   }
 * };
 * ```
 *
 * ## 戻り値
 * - uploadFiles: ファイルアップロード実行関数
 * - uploadProgress: ファイル別アップロード進捗（0-100）
 * - isUploading: アップロード中フラグ
 * - error: エラーメッセージ
 * - clearError: エラークリア関数
 */
export function useS3Upload(): UseS3UploadReturn {
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const clearError = () => setError(null);

  const uploadFiles = async (
    files: File[],
    accountId: string,
    authHeaders?: Record<string, string>,
  ): Promise<InstructionSheetConverterResponse> => {
    setIsUploading(true);
    setError(null);
    setUploadProgress({});

    try {
      // Step 1: 署名付きURL取得
      console.log('署名付きURL取得開始:', files.length, 'ファイル');
      const presignedResponse = await requestPresignedUrls(files, accountId, authHeaders);
      console.log('署名付きURL取得完了:', presignedResponse.job_id);

      // Step 2: 各ファイルをS3に直接アップロード
      console.log('S3アップロード開始');
      const uploadPromises = presignedResponse.upload_urls.map(async (urlInfo) => {
        const file = files.find((f) => f.name === urlInfo.filename);
        if (!file) {
          throw new Error(`ファイルが見つかりません: ${urlInfo.filename}`);
        }

        console.log(`ファイルアップロード開始: ${urlInfo.filename}`);
        await uploadFileToS3(file, urlInfo.upload_url, (progress) => {
          setUploadProgress((prev) => ({
            ...prev,
            [urlInfo.filename]: progress,
          }));
        });

        console.log(`ファイルアップロード完了: ${urlInfo.filename}`);
        return {
          filename: urlInfo.filename,
          s3_key: urlInfo.s3_key,
          size: file.size,
        } as UploadedFileInfo;
      });

      const uploadedFiles = await Promise.all(uploadPromises);
      console.log('全ファイルアップロード完了:', uploadedFiles.length, 'ファイル');

      // Step 3: アップロード完了通知
      console.log('アップロード完了通知送信');
      const response = await notifyUploadComplete(
        presignedResponse.job_id,
        uploadedFiles,
        accountId,
        authHeaders,
      );
      console.log('変換処理開始:', response.job_id);

      return response;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'アップロードに失敗しました';
      console.error('S3アップロードエラー:', errorMessage);
      setError(errorMessage);
      throw err;
    } finally {
      setIsUploading(false);
    }
  };

  return {
    uploadFiles,
    uploadProgress,
    isUploading,
    error,
    clearError,
  };
}
